import {
	Button,
	Card,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Select,
	Typography,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";

import dayjs from "dayjs";
import React, { Fragment, useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";

import { useDispatch, useSelector } from "react-redux";
import { addSingleProject, loadAllProject, updateProject } from "../../redux/rtk/features/projectManagement/project/project/project";
import { loadProjectSiteDetails } from "../../redux/rtk/features/projectManagement/project/projectSite/projectSiteDetails";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import Project from "./project";
import "./Project.css";
import "../hiring/FilterPanel.css";

const AddProject = ({ drawer }) => {
	const [loader, setLoader] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editingProject, setEditingProject] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const userList = useSelector((state) => state?.users?.list);
	const { list: projectSiteList = [] } = useSelector((state) => state?.projectSiteDetails || {});

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(loadAllStaff({ status: "true" }));
		dispatch(loadAllProject());
		dispatch(loadProjectSiteDetails());
	}, [dispatch]);

	const [form] = Form.useForm();

	const parseNumber = (value) => {
		if (value === undefined || value === null || value === "") {
			return null;
		}
		const numeric = Number(value);
		return Number.isNaN(numeric) ? null : numeric;
	};

	const parseNumberOrString = (value) => {
		const numeric = parseNumber(value);
		return numeric === null ? value : numeric;
	};

	const resetEditState = () => {
		form.resetFields();
		setEditingProject(null);
	};

	const openCreateModal = () => {
		resetEditState();
		setIsModalVisible(true);
	};

	const handleEditProject = (project) => {
		if (!project) {
			return;
		}

		const projectSiteId = parseNumber(project?.ProjectSiteId ?? project?.projectSiteId);
		const projectManagerId = parseNumber(project?.ProjectManagerId ?? project?.projectManagerId);
		const totalBudgetRaw = project?.TotalBudget ?? project?.totalBudget;
		const startDateValue = project?.StartDate ?? project?.startDate ?? null;
		const endDateValue = project?.EndDate ?? project?.endDate ?? null;

		form.setFieldsValue({
			projectSiteId: projectSiteId ?? undefined,
			projectManagerId: projectManagerId ?? undefined,
			projectName: project?.ProjectName ?? project?.projectName ?? "",
			projectDescription: project?.ProjectDescription ?? project?.projectDescription ?? "",
			startDate: startDateValue ? dayjs(startDateValue) : null,
			endDate: endDateValue ? dayjs(endDateValue) : null,
			totalBudget:
				totalBudgetRaw !== undefined && totalBudgetRaw !== null ? Number(totalBudgetRaw) : null,
		});

		setEditingProject({
			id: project?.ProjectId ?? project?.projectId ?? project?.id ?? null,
			dataStatus: project?.DataStatus ?? project?.dataStatus ?? 1,
		});
		setIsModalVisible(true);
	};

	const handleCancelEdit = () => {
		resetEditState();
		setIsModalVisible(false);
	};

	const projectManagerOptions = useMemo(() => {
		if (!Array.isArray(userList)) {
			return [];
		}
		return userList
			.map((item) => {
				const rawId = item?.EmployeeId ?? item?.id;
				const value = rawId !== undefined && rawId !== null ? parseNumberOrString(rawId) : undefined;
				const label = item?.EmployeeName
					? item.EmployeeName
					: [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim();
				if (value === undefined || value === null || !label) {
					return null;
				}
				return {
					value,
					label,
				};
			})
			.filter(Boolean);
	}, [userList]);

	const projectSiteOptions = useMemo(() => {
		if (!Array.isArray(projectSiteList)) {
			return [];
		}
		return projectSiteList
			.map((item) => {
				const siteId = item?.ProjectSiteId ?? item?.id;
				const value = siteId !== undefined && siteId !== null ? parseNumberOrString(siteId) : undefined;
				const label = item?.ProjectSiteName || item?.projectSiteName;
				if (value === undefined || value === null || !label) {
					return null;
				}
				return {
					value,
					label,
				};
			})
			.filter(Boolean);
	}, [projectSiteList]);

	const onFinish = async (values) => {
		const basePayload = {
			ProjectSiteId: parseNumber(values?.projectSiteId),
			ProjectName: values?.projectName ?? values?.name ?? "",
			ProjectDescription: values?.projectDescription ?? values?.description ?? "",
			ProjectManagerId: parseNumber(values?.projectManagerId),
			StartDate: values?.startDate ? dayjs(values.startDate).format("YYYY-MM-DD") : null,
			EndDate: values?.endDate ? dayjs(values.endDate).format("YYYY-MM-DD") : null,
			TotalBudget:
				values?.totalBudget !== undefined && values?.totalBudget !== null
					? Number(values.totalBudget)
					: null,
			DataStatus: editingProject?.dataStatus ?? 1,
		};

		const payload = {
			...basePayload,
			ProjectId: editingProject?.id ?? 0,
		};

		setLoader(true);
		let resp;
		if (editingProject?.id) {
			resp = await dispatch(updateProject({ id: editingProject.id, values: payload }));
		} else {
			resp = await dispatch(addSingleProject(payload));
		}
		setLoader(false);

		if (resp?.meta?.requestStatus !== "rejected") {
			resetEditState();
			dispatch(loadAllProject());
			setIsModalVisible(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Failed at adding Project");
		setLoader(false);
	};

	return (
		<Fragment>
			<Card
				className="mr-top"
				title={
					<Typography.Title level={4} style={{ margin: 0 }}>
						Project List
					</Typography.Title>
				}
				extra={
					<>
						<button
							type="button"
							className="filter-panel-trigger"
							onClick={() => setFilterOpen(true)}
							aria-label="Filters"
							style={{ marginRight: 8 }}
						>
							<FilterOutlined style={{ fontSize: 16 }} />
						</button>
						<Button type="primary" className="project-apply-btn" onClick={openCreateModal}>
							Add Project
						</Button>
					</>
				}
				bodyStyle={{ padding: 0 }}
			>
				<Project
					onEditProject={handleEditProject}
					filterOpen={filterOpen}
					onFilterClose={() => setFilterOpen(false)}
				/>
			</Card>

			<Modal
				title={editingProject ? "Update Project" : "Add Project"}
				open={isModalVisible}
				onCancel={handleCancelEdit}
				onOk={() => form.submit()}
				okText={editingProject ? "Update" : "Create"}
				cancelText="Cancel"
				confirmLoading={loader}
				width={720}
				destroyOnClose
			>
				<Form
					form={form}
					name="project-form"
					labelCol={{ span: 7 }}
					wrapperCol={{ span: 16 }}
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
					autoComplete="off"
					layout="horizontal"
				>
					<Form.Item
						label="Project Site"
						name="projectSiteId"
						rules={[{ required: true, message: "Select Project Site" }]}
					>
						<Select
							loading={!projectSiteOptions.length}
							showSearch
							placeholder="Select Project Site"
							optionFilterProp="label"
							options={projectSiteOptions}
						/>
					</Form.Item>

					<Form.Item
						label="Project Manager"
						name="projectManagerId"
						rules={[{ required: true, message: "Select Project Manager" }]}
					>
						<Select
							loading={!projectManagerOptions.length}
							showSearch
							placeholder="Select Project Manager"
							optionFilterProp="label"
							options={projectManagerOptions}
							allowClear
						/>
					</Form.Item>

					<Form.Item
						label="Project Name"
						name="projectName"
						rules={[{ required: true, message: "Enter Project Name" }]}
					>
						<Input placeholder="Enter Project Name" />
					</Form.Item>

					<Form.Item
						label="Start Date"
						name="startDate"
						rules={[{ required: true, message: "Please input Project Start Date!" }]}
					>
						<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
					</Form.Item>

					<Form.Item
						label="End Date"
						name="endDate"
						rules={[{ required: true, message: "Please input Project End Date!" }]}
					>
						<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
					</Form.Item>

					<Form.Item
						label="Total Budget"
						name="totalBudget"
						rules={[{ required: true, message: "Enter Total Budget" }]}
					>
						<InputNumber
							style={{ width: "100%" }}
							placeholder="Enter Total Budget"
							min={0}
							step={0.01}
							stringMode
						/>
					</Form.Item>

					<Form.Item
						label="Description"
						name="projectDescription"
						rules={[{ required: true, message: "Enter Project Description" }]}
					>
						<Input.TextArea placeholder="Enter Project Description" rows={3} />
					</Form.Item>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default AddProject;
